import { Box, Icon, Text } from '@factorialco/deprecated-design-system'
import { flatten, isEmpty, uniq } from 'lodash'
import moment from 'moment'

import { observer } from 'mobx-react'

import { FormatTypes, convertDate, dateToString } from 'modules/core/lib/dates'
import { employeeUser } from 'modules/core/store/collections/users'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useSelectableEmployees } from 'modules/shift_management/hooks/useSelectableEmployees'
import { useSelectionState } from 'modules/shift_management/hooks/useSelection'

import NamesToDisplay from './NamesToDisplay'

export interface Props {
  employeeIds?: number[]
  date?: moment.Moment
  showDates: boolean
  contractVersions: ContractVersions
}

const SelectedCellsInfo = ({ employeeIds = [], date, showDates, contractVersions }: Props) => {
  const [{ view }] = useSchedule()
  const { daysByRow } = useSelectionState()

  const { selectableEmployees } = useSelectableEmployees(view.startAt, view.endAt, contractVersions)

  const userIds = isEmpty(employeeIds)
    ? new Set(Object.keys(daysByRow))
    : new Set(employeeIds.map(String) || '')
  const userNames = selectableEmployees
    .filter((employee) => userIds.has(String(employee.id)))
    .map((employee) => employeeUser(employee).fullName())

  const sortedNames = userNames.sort((a, b) => a.localeCompare(b))

  const selectionDates = uniq(flatten(Object.values(daysByRow))).sort((a, b) =>
    moment(a).diff(moment(b))
  )
  const selectionStartDate = selectionDates[0]
  const selectionEndDate = selectionDates[selectionDates.length - 1]

  const dateFormat: FormatTypes = view.isDaily ? 'ui_short_with_time' : 'backend'
  const convertedSelectionStartDate = selectionStartDate
    ? convertDate(selectionStartDate, dateFormat, 'ui_long')
    : ''
  const convertedSelectionEndDate = selectionEndDate
    ? convertDate(selectionEndDate, dateFormat, 'ui_long')
    : ''

  const startDate = date ? dateToString(date, 'ui_long') : convertedSelectionStartDate
  const endDate = date ? dateToString(date, 'ui_long') : convertedSelectionEndDate

  const sameDate = startDate === endDate

  const singleDay = selectionStartDate
    ? convertDate(selectionStartDate, dateFormat, 'ui_long_with_year')
    : ''
  const start = selectionStartDate
    ? convertDate(selectionStartDate, dateFormat, 'ui_abbreviated_month_year')
    : ''
  const end = selectionEndDate
    ? convertDate(selectionEndDate, dateFormat, 'ui_abbreviated_month_year')
    : ''

  const periodExplanation = sameDate ? singleDay : `${start} - ${end}`

  return (
    <Box
      borderRadius={{ all: 'abs008' }}
      padding="s16"
      gap="s12"
      border={{
        all: {
          style: 'solid',
          width: 's1',
          color: 'grey400',
        },
      }}
      background="grey100"
    >
      {view.isByEmployees && (
        <Box flexDirection="row" gap="s8">
          <Box>
            <Icon.Medium icon="person" color="grey700" />
          </Box>
          <Box>
            <NamesToDisplay items={sortedNames} maxItemsToDisplay={4} />
          </Box>
        </Box>
      )}
      {showDates && (
        <Box flexDirection="row" gap="s8">
          <Box>
            <Icon.Medium icon="calendar" color="grey700" />
          </Box>
          <Box>
            <Text.Normal size="300" color="grey1000">
              {periodExplanation}
            </Text.Normal>
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default observer(SelectedCellsInfo)
